import React, { useState } from 'react';
import { AiOutputPreview } from '../../pages/PromptDetail/AiOutputPreview';
import { PromptTextPreview } from '../../pages/PromptDetail/PromptTextPreview';
import { ScorePanel } from '../../pages/PromptDetail/ScorePanel';
import { apiService } from '../../services';
import type { PromptDetails } from '../../types';

export const WorkedExample: React.FC = () => {
  const [promptId, setPromptId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<PromptDetails | null>(null);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const id = promptId.trim();
    if (!id || loading) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getPromptDetails(id);
      if (response.success && response.data) {
        setPrompt(response.data);
      } else {
        setPrompt(null);
        setError(response.message || 'Could not load that prompt.');
      }
    } catch (err) {
      setPrompt(null);
      setError(err instanceof Error ? err.message : 'Could not load that prompt.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="sc-sect">
      <h3>Worked example</h3>
      <p className="sc-lead">
        Paste a prompt ID to load that prompt, the AI output, and the score breakdown.
      </p>
      <form className="sc-lookup" onSubmit={onSubmit}>
        <label className="sc-lookup__field">
          <span className="sc-sub">Prompt ID</span>
          <input
            type="text"
            value={promptId}
            onChange={(event) => setPromptId(event.target.value)}
            placeholder="Paste a prompt ID"
            aria-label="Prompt ID"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <button type="submit" className="sc-lookup__btn" disabled={loading || !promptId.trim()}>
          {loading ? 'Loading…' : 'Show breakdown'}
        </button>
      </form>
      {error && <p className="sc-lookup__error" role="alert">{error}</p>}
      {prompt && (
        <div className="sc-example">
          <div className="sc-example__io">
            <PromptTextPreview promptText={prompt.prompt_text} />
            <AiOutputPreview aiSummary={prompt.ai_summary} />
          </div>
          <ScorePanel prompt={prompt} />
        </div>
      )}
    </section>
  );
};
